/**
 * AgentNetworkBoard — the SVG board for the Adversarial Robustness game.
 *
 * Renders the agent network: honest agents in green (circle), compromised
 * agents in red (diamond). Governed-interface edges are drawn between every
 * pair; an edge that is currently a LEGAL square (factors through a
 * compromised agent) is highlighted as part of the reachable violation
 * surface, while honest-to-honest edges stay inert — the move they would
 * carry is structurally inexpressible.
 *
 * The board renders state only; all logic lives in algorithms/. It is
 * keyboard-playable: agents are focusable buttons and edges are focusable
 * when they are legal squares. Encoding is never color-only — shape (circle
 * vs diamond), label text, and dashing carry the same information.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import type { AgentId, Scenario } from "../algorithms/types.js";
import { panelTokens } from "@csk/demo-ui";
import { isLegalSquare, pairKey } from "../algorithms/rules.js";
const SIZE = 460;
export interface AgentNetworkBoardProps {
  readonly scenario: Scenario;
  readonly compromised: ReadonlySet<AgentId>;
  readonly realizedKeys: ReadonlySet<string>;
  readonly selected: AgentId | null;
  readonly onToggleAgent: (id: AgentId) => void;
  readonly onAttemptEdge: (a: AgentId, b: AgentId) => void;
}
export function AgentNetworkBoard({
  scenario,
  compromised,
  realizedKeys,
  selected,
  onToggleAgent,
  onAttemptEdge,
}: AgentNetworkBoardProps): React.JSX.Element {
  const pos = new Map<
    AgentId,
    {
      x: number;
      y: number;
    }
  >();
  for (const agent of scenario.agents) {
    pos.set(agent.id, { x: agent.x * SIZE, y: agent.y * SIZE });
  }
  return (
    <svg
      viewBox={`0 0 ${String(SIZE)} ${String(SIZE)}`}
      width="100%"
      role="group"
      aria-label={`${scenario.title} agent network`}
      className="mx-auto block max-w-[520px]"
    >
      {scenario.interfaces.map((iface) => {
        const pa = pos.get(iface.a);
        const pb = pos.get(iface.b);
        if (!pa || !pb) return null;
        const legal = isLegalSquare(scenario, compromised, iface.a, iface.b);
        const key = pairKey(iface.a, iface.b);
        const realized = realizedKeys.has(key);
        const stroke = realized
          ? panelTokens.adversary
          : legal
            ? panelTokens.surfaceBound
            : panelTokens.interface;
        const label = legal
          ? realized
            ? `Realized forbidden square ${iface.a} to ${iface.b}`
            : `Reachable forbidden square ${iface.a} to ${iface.b} — attempt it`
          : `Governed interface ${iface.a} to ${iface.b} — honest pair, no forbidden square is constructable here`;
        return (
          <line
            key={key}
            x1={pa.x}
            y1={pa.y}
            x2={pb.x}
            y2={pb.y}
            stroke={stroke}
            strokeWidth={realized ? 4 : legal ? 3 : 1.4}
            strokeOpacity={legal ? 0.95 : 0.32}
            strokeDasharray={legal ? undefined : "5 6"}
            tabIndex={legal ? 0 : -1}
            role={legal ? "button" : "presentation"}
            aria-label={label}
            className={legal ? "cursor-pointer" : "cursor-default"}
            onClick={() => {
              if (legal) onAttemptEdge(iface.a, iface.b);
            }}
            onKeyDown={(event) => {
              if (legal && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                onAttemptEdge(iface.a, iface.b);
              }
            }}
          />
        );
      })}

      {scenario.agents.map((agent) => {
        const p = pos.get(agent.id);
        if (!p) return null;
        const isAdv = compromised.has(agent.id);
        const isSel = selected === agent.id;
        const fill = isAdv ? panelTokens.adversary : panelTokens.honest;
        const r = 23;
        return (
          <g
            key={agent.id}
            tabIndex={0}
            role="button"
            aria-pressed={isAdv}
            aria-label={`${agent.name}, ${isAdv ? "compromised (adversarial)" : "honest"}${isSel ? ", selected" : ""}. Activate to ${isAdv ? "restore" : "compromise"}.`}
            className="cursor-pointer"
            onClick={() => {
              onToggleAgent(agent.id);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onToggleAgent(agent.id);
              }
            }}
          >
            {isAdv ? (
              <rect
                x={p.x - r}
                y={p.y - r}
                width={r * 2}
                height={r * 2}
                transform={`rotate(45 ${String(p.x)} ${String(p.y)})`}
                fill={`${fill}22`}
                stroke={fill}
                strokeWidth={isSel ? 4 : 2.5}
              />
            ) : (
              <circle
                cx={p.x}
                cy={p.y}
                r={r}
                fill={`${fill}22`}
                stroke={fill}
                strokeWidth={isSel ? 4 : 2.5}
              />
            )}
            <text
              x={p.x}
              y={p.y - 1}
              textAnchor="middle"
              fontSize={13}
              fontWeight={700}
              fill={panelTokens.bright}
              className="pointer-events-none"
            >
              {agent.id}
            </text>
            <text
              x={p.x}
              y={p.y + 12}
              textAnchor="middle"
              fontSize={8.5}
              fill={fill}
              className="pointer-events-none"
            >
              {isAdv ? "ADV" : "honest"}
            </text>
            <text
              x={p.x}
              y={p.y + r + 13}
              textAnchor="middle"
              fontSize={9.5}
              fill={panelTokens.muted}
              className="pointer-events-none"
            >
              {agent.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
